import React, { useState } from 'react';
import { Briefcase, Upload, FileText, User, Mail, Phone, MapPin, Clock, Users, CheckCircle, X } from 'lucide-react';
import axios from 'axios';
import './Careers.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg).filter(Boolean).join(', ') || 'Please check your details and try again.';
  }
  return 'Failed to submit application. Please try again.';
};

const positions = [
  { value: 'bartender', label: 'Bartender', description: 'Mix drinks and provide excellent customer service' },
  { value: 'cook', label: 'Cook', description: 'Prepare delicious meals in our kitchen' },
  { value: 'bar_back', label: 'Bar Back', description: 'Support bartenders and maintain bar area' },
  { value: 'dishwasher', label: 'Dishwasher', description: 'Keep our kitchen clean and organized' },
  { value: 'stocker', label: 'Stocker', description: 'Manage inventory and stock supplies' }
];

const Careers = () => {
  const [applicationMode, setApplicationMode] = useState(null); // 'resume' or 'manual'
  const [selectedPosition, setSelectedPosition] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Resume upload form data
  const [resumeFormData, setResumeFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  // Manual form data
  const [manualFormData, setManualFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    experience: '',
    availability: '',
    references: [
      { name: '', phone: '', relationship: '' },
      { name: '', phone: '', relationship: '' },
      { name: '', phone: '', relationship: '' }
    ]
  });

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a PDF, PNG, or JPEG file');
        return;
      }
      setResumeFile(file);
      setError('');
    }
  };

  const handleResumeFormChange = (e) => {
    setResumeFormData({
      ...resumeFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleManualFormChange = (e) => {
    setManualFormData({
      ...manualFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleReferenceChange = (index, field, value) => {
    const newReferences = [...manualFormData.references];
    newReferences[index][field] = value;
    setManualFormData({
      ...manualFormData,
      references: newReferences
    });
  };

  const addReference = () => {
    setManualFormData({
      ...manualFormData,
      references: [...manualFormData.references, { name: '', phone: '', relationship: '' }]
    });
  };

  const removeReference = (index) => {
    if (manualFormData.references.length > 3) {
      const newReferences = manualFormData.references.filter((_, i) => i !== index);
      setManualFormData({
        ...manualFormData,
        references: newReferences
      });
    }
  };

  const handleResumeSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPosition || !resumeFile) {
      setError('Please select a position and upload your resume');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('position', selectedPosition);
      formData.append('first_name', resumeFormData.first_name);
      formData.append('last_name', resumeFormData.last_name);
      formData.append('email', resumeFormData.email);
      formData.append('phone', resumeFormData.phone);
      formData.append('resume', resumeFile);

      await axios.post(`${API}/careers/apply-with-resume`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPosition) {
      setError('Please select a position');
      return;
    }

    // Validate references
    const validReferences = manualFormData.references.filter(ref => ref.name && ref.phone);
    if (validReferences.length < 3) {
      setError('Please provide at least 3 references');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/careers/apply`, {
        position: selectedPosition,
        ...manualFormData,
        references: validReferences
      });

      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setApplicationMode(null);
    setSelectedPosition('');
    setResumeFile(null);
    setSuccess(false);
    setError('');
    setResumeFormData({ first_name: '', last_name: '', email: '', phone: '' });
    setManualFormData({
      first_name: '', last_name: '', email: '', phone: '',
      address: '', city: '', state: '', zip_code: '',
      experience: '', availability: '',
      references: [
        { name: '', phone: '', relationship: '' },
        { name: '', phone: '', relationship: '' },
        { name: '', phone: '', relationship: '' }
      ]
    });
  };

  if (success) {
    return (
      <section className="careers-section" id="careers">
        <div className="careers-container">
          <div className="success-message" data-testid="application-success">
            <div className="success-check" data-testid="success-check-animation" aria-hidden="true">
              <svg viewBox="0 0 52 52" className="success-check-svg">
                <circle className="success-check-circle" cx="26" cy="26" r="24" fill="none" />
                <path className="success-check-mark" fill="none" d="M14 27 l8 8 l16 -16" />
              </svg>
            </div>
            <h2>Application Submitted!</h2>
            <p>Thank you for your interest in joining Royal Bar & Grill. We'll review your application and contact you soon.</p>
            <button onClick={resetForm} className="btn-primary">Submit Another Application</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="careers-section" id="careers">
      <div className="careers-container">
        <div className="careers-header">
          <h1>Join Our Team</h1>
          <p>We're always looking for talented individuals to join the Royal Bar & Grill family</p>
        </div>

        {/* Position Selection */}
        <div className="positions-grid">
          <h3><Briefcase size={20} /> Open Positions</h3>
          <div className="positions-list">
            {positions.map((pos) => (
              <div 
                key={pos.value}
                className={`position-card ${selectedPosition === pos.value ? 'selected' : ''}`}
                onClick={() => setSelectedPosition(pos.value)}
                data-testid={`position-${pos.value}`}
              >
                <h4>{pos.label}</h4>
                <p>{pos.description}</p>
                {selectedPosition === pos.value && <CheckCircle size={20} className="check-icon" />}
              </div>
            ))}
          </div>
        </div>

        {selectedPosition && !applicationMode && (
          <div className="application-choice" data-testid="application-choice">
            <h3>How would you like to apply?</h3>
            <div className="choice-buttons">
              <button 
                className="choice-btn resume-btn"
                onClick={() => setApplicationMode('resume')}
                data-testid="choose-resume"
              >
                <Upload size={32} />
                <span>Upload Resume</span>
                <small>PDF, PNG, or JPEG</small>
              </button>
              <button 
                className="choice-btn manual-btn"
                onClick={() => setApplicationMode('manual')}
                data-testid="choose-manual"
              >
                <FileText size={32} />
                <span>Fill Out Form</span>
                <small>Complete application manually</small>
              </button>
            </div>
          </div>
        )}

        {error && <div className="error-message" data-testid="error-message">{error}</div>}

        {/* Resume Upload Form */}
        {applicationMode === 'resume' && (
          <form onSubmit={handleResumeSubmit} className="application-form resume-form" data-testid="resume-form">
            <div className="form-header">
              <h3><Upload size={20} /> Upload Your Resume</h3>
              <button type="button" className="btn-back" onClick={() => setApplicationMode(null)}>
                <X size={18} /> Back
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><User size={16} /> First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={resumeFormData.first_name}
                  onChange={handleResumeFormChange}
                  required
                  placeholder="John"
                />
              </div>
              <div className="form-group">
                <label><User size={16} /> Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={resumeFormData.last_name}
                  onChange={handleResumeFormChange}
                  required
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label><Mail size={16} /> Email *</label>
                <input
                  type="email"
                  name="email"
                  value={resumeFormData.email}
                  onChange={handleResumeFormChange}
                  required
                  placeholder="john@example.com"
                />
              </div>
              <div className="form-group">
                <label><Phone size={16} /> Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={resumeFormData.phone}
                  onChange={handleResumeFormChange}
                  required
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="form-group file-upload-group">
              <label><FileText size={16} /> Resume *</label>
              <div className="file-upload-area" onClick={() => document.getElementById('resume-input').click()}>
                {resumeFile ? (
                  <div className="file-selected">
                    <FileText size={24} />
                    <span>{resumeFile.name}</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setResumeFile(null); }}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={32} />
                    <span>Click to upload or drag and drop</span>
                    <small>PDF, PNG, or JPEG (Max 10MB)</small>
                  </>
                )}
              </div>
              <input
                id="resume-input"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleResumeChange}
                style={{ display: 'none' }}
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading} data-testid="submit-resume">
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        )}

        {/* Manual Application Form */}
        {applicationMode === 'manual' && (
          <form onSubmit={handleManualSubmit} className="application-form manual-form" data-testid="manual-form">
            <div className="form-header">
              <h3><FileText size={20} /> Application Form</h3>
              <button type="button" className="btn-back" onClick={() => setApplicationMode(null)}>
                <X size={18} /> Back
              </button>
            </div>

            {/* Personal Information */}
            <div className="form-section">
              <h4><User size={18} /> Personal Information</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={manualFormData.first_name}
                    onChange={handleManualFormChange}
                    required
                    placeholder="John"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={manualFormData.last_name}
                    onChange={handleManualFormChange}
                    required
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><Mail size={16} /> Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={manualFormData.email}
                    onChange={handleManualFormChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>
                <div className="form-group">
                  <label><Phone size={16} /> Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={manualFormData.phone}
                    onChange={handleManualFormChange}
                    required
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="form-group">
                <label><MapPin size={16} /> Address</label>
                <input
                  type="text"
                  name="address"
                  value={manualFormData.address}
                  onChange={handleManualFormChange}
                  placeholder="123 Main Street"
                />
              </div>

              <div className="form-row triple">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={manualFormData.city}
                    onChange={handleManualFormChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={manualFormData.state}
                    onChange={handleManualFormChange}
                    placeholder="State"
                  />
                </div>
                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={manualFormData.zip_code}
                    onChange={handleManualFormChange}
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>

            {/* Experience & Availability */}
            <div className="form-section">
              <h4><Briefcase size={18} /> Experience & Availability</h4>
              
              <div className="form-group">
                <label>Relevant Experience</label>
                <textarea
                  name="experience"
                  value={manualFormData.experience}
                  onChange={handleManualFormChange}
                  placeholder="Describe your relevant work experience..."
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label><Clock size={16} /> Availability</label>
                <textarea
                  name="availability"
                  value={manualFormData.availability}
                  onChange={handleManualFormChange}
                  placeholder="When are you available to work? (e.g., Weekends, Evenings, Full-time)"
                  rows={2}
                />
              </div>
            </div>

            {/* References */}
            <div className="form-section">
              <h4><Users size={18} /> References (Minimum 3)</h4>
              
              {manualFormData.references.map((ref, index) => (
                <div key={`reference-${index}-${ref.name || 'empty'}`} className="reference-card">
                  <div className="reference-header">
                    <span>Reference {index + 1}</span>
                    {manualFormData.references.length > 3 && (
                      <button type="button" onClick={() => removeReference(index)} className="btn-remove-ref">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="form-row triple">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={ref.name}
                        onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                        placeholder="Full name"
                        required={index < 3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone *</label>
                      <input
                        type="tel"
                        value={ref.phone}
                        onChange={(e) => handleReferenceChange(index, 'phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        required={index < 3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Relationship *</label>
                      <input
                        type="text"
                        value={ref.relationship}
                        onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                        placeholder="e.g., Former Manager"
                        required={index < 3}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button type="button" onClick={addReference} className="btn-add-ref">
                + Add Another Reference
              </button>
            </div>

            <button type="submit" className="btn-submit" disabled={loading} data-testid="submit-manual">
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default Careers;
